import { useState, createContext, useContext, useRef, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { styled, alpha, useTheme } from '@mui/material/styles';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar, { DRAWER_WIDTH } from './DashboardSidebar';
import RightCategoriesPanel from './RightCategoriesPanel';
import BottomNav, { BOTTOM_NAV_HEIGHT } from './BottomNav';
import { PHONE_WIDTH } from './layoutConstants';

export { PHONE_WIDTH };

const APPBAR_MOBILE = 56;
const APPBAR_DESKTOP = 56;


const RootStyle = styled('div')({
  display: 'flex',
  height: '100dvh',
  maxHeight: '100dvh',
  overflow: 'hidden',
});

const MainStyle = styled('main')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollBehavior: 'auto',
  height: `calc(100% - ${APPBAR_MOBILE}px)`,
  marginTop: APPBAR_MOBILE,
  paddingTop: 8,
  // The bar is BOTTOM_NAV_HEIGHT plus its own 6px top/bottom padding, and
  // content (CTAs in empty states, last feed row) was landing underneath it.
  // Reserve the real occupied height plus a comfortable gutter.
  paddingBottom: `calc(${BOTTOM_NAV_HEIGHT + 12}px + env(safe-area-inset-bottom, 0px) + ${theme.spacing(3)})`,

  // Elegant scrollbar styling
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.primary, 0.12),
    borderRadius: 8,
    '&:hover': {
      background: alpha(theme.palette.text.primary, 0.24),
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.text.primary, 0.12)} transparent`,

  [theme.breakpoints.up('lg')]: {
    height: `calc(100% - ${APPBAR_DESKTOP}px)`,
    marginTop: APPBAR_DESKTOP,
    paddingTop: 8,
    paddingBottom: theme.spacing(3),
  },
}));

// Context so pages can inject content into the navbar
const NavbarContext = createContext({
  setCategoryTabs: () => {},
  setVisibleCategory: () => {},
  visibleCategory: null,
  setNavbarVisible: () => {},
});
export const useNavbar = () => useContext(NavbarContext);

export default function DashboardLayout() {
  const theme = useTheme();
  const [categoryTabs, setCategoryTabs] = useState(null);
  const [visibleCategory, setVisibleCategory] = useState(null);
  const { pathname } = useLocation();

  // Fullscreen experiences render their own chrome and should sit at the
  // top of the viewport with nothing above them.
  const fullscreen = /^\/play\/mindmeld\/.+/.test(pathname);

  const isGameplay =
    /^\/play\/rapid/.test(pathname) ||
    /^\/play\/contrarian/.test(pathname) ||
    /^\/play\/streak/.test(pathname) ||
    /^\/play\/blindbet/.test(pathname) ||
    /^\/play\/daily/.test(pathname) ||
    /^\/play\/category\/.+/.test(pathname) ||
    /^\/play\/versus\/.+/.test(pathname) ||
    /^\/play\/agree\/.+/.test(pathname) ||
    /^\/play\/mindmeld\/.+/.test(pathname) ||
    /^\/play\/memory\/.+/.test(pathname);

  // BottomNav hides on /new, so the default bottom-nav padding would push
  // CreatorStudio's Back/Next/Publish bar below the fold.
  const isCreate = pathname === '/new';

  const isFeedPage = pathname === '/dashboard/app' || pathname === '/';
  const isVotePage = /^\/v\/[^/]+/.test(pathname);

  const [navbarVisible, setNavbarVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const mainRef = useRef(null);

  useEffect(() => {
    const mainEl = mainRef.current;
    if (!mainEl) return;

    let lastScrollTop = 0;
    let scrollUpAccumulator = 0;
    const SCROLL_UP_THRESHOLD = 150; // require 150px of upward scroll to show the navbar/menu again

    const handleScroll = (e) => {
      if (!isFeedPage) {
        setNavbarVisible(true);
        setScrolled(false);
        return;
      }
      const target = e?.target || mainEl;
      if (!target) return;
      const scrollTop = typeof target.scrollTop === 'number' ? target.scrollTop : 0;
      setScrolled(scrollTop > 65);

      if (scrollTop < 80) {
        setVisibleCategory(null);
        setNavbarVisible(true);
        scrollUpAccumulator = 0;
      } else if (scrollTop > lastScrollTop) {
        // Scrolling down -> hide header immediately
        setNavbarVisible(false);
        scrollUpAccumulator = 0; // reset accumulated scroll up distance
      } else {
        // Scrolling up -> track distance scrolled up
        const delta = lastScrollTop - scrollTop;
        scrollUpAccumulator += delta;
        if (scrollUpAccumulator > SCROLL_UP_THRESHOLD) {
          setNavbarVisible(true);
        }
      }
      lastScrollTop = scrollTop;
    };

    mainEl.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => mainEl.removeEventListener('scroll', handleScroll, { capture: true });
  }, [isFeedPage]);

  return (
    <NavbarContext.Provider value={{ setCategoryTabs, setVisibleCategory, visibleCategory, setNavbarVisible }}>
      <RootStyle>
        {!fullscreen && <DashboardNavbar transparent={isVotePage} categoryTabs={categoryTabs} visible={navbarVisible} />}
        <DashboardSidebar />
        <RightCategoriesPanel show={isFeedPage && !navbarVisible} visibleCategory={visibleCategory} />
        <MainStyle
          ref={mainRef}
          sx={{
            ...(fullscreen ? { paddingTop: 0 } : {}),
            ...(isGameplay ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' } : {}),
            ...(isCreate ? { paddingBottom: 'env(safe-area-inset-bottom, 0px)' } : {}),
            ...(isFeedPage ? {
              marginTop: `${APPBAR_MOBILE}px`,
              height: `calc(100% - ${APPBAR_MOBILE}px)`,
              paddingTop: 0,
              paddingBottom: 0,
              [theme.breakpoints.down('md')]: {
                overflow: 'hidden',
              },
              [theme.breakpoints.up('md')]: {
                paddingBottom: `calc(${BOTTOM_NAV_HEIGHT + 12}px + env(safe-area-inset-bottom, 0px) + 24px)`,
              },
              // Explore uses the FULL desktop width - it is a browse surface
              // and wants as many tiles per row as the screen allows. Only the
              // post view (/v/) is phone-shaped on desktop.
              [theme.breakpoints.up('lg')]: {
                marginTop: `${APPBAR_DESKTOP}px`,
                height: `calc(100% - ${APPBAR_DESKTOP}px)`,
                paddingTop: '20px',
                paddingBottom: '24px',
              },
            } : {}),
            ...(isVotePage ? {
              marginTop: 0,
              height: '100%',
              paddingTop: 0,
              paddingBottom: 0,
              overflow: 'hidden',
              // Desktop: the navbar is transparent and floats over the vote
              // surface, which is right for the mobile full-bleed card, but
              // on a wide viewport the cards are top-anchored and collided
              // with the floating search pill - clipping the top of both
              // images. Reserve the navbar's height here only.
              [theme.breakpoints.up('lg')]: {
                marginTop: 0,
                height: `calc(100% - ${APPBAR_DESKTOP}px)`,
                paddingTop: `${APPBAR_DESKTOP}px`,
                paddingBottom: 0,
                overflow: 'hidden',
                // The vote page is the TikTok-style vertical slider, and stays
                // phone-shaped on desktop: a centred phone-width column rather
                // than a stretched wide layout.
                maxWidth: PHONE_WIDTH,
                marginLeft: `calc(50vw - ${PHONE_WIDTH / 2}px - ${DRAWER_WIDTH}px)`,
                marginRight: 'auto',
              },
            } : {}),
          }}
        >
          <Outlet />
        </MainStyle>
        <BottomNav />
      </RootStyle>
    </NavbarContext.Provider>
  );
}
